<template>
    <div>
        <b-row>
            <b-col md="12">
                <b-card
                    no-body
                >
                    <b-card-header>
                        <b-card-title>
                            <span>{{$t("org.auth.settingOtp")}}</span>
                        </b-card-title>
                    </b-card-header>
                    <template v-if="userInfo.otpEnabled">
                        <div class="text-center">
                            <i class="mb-1 fas fa-user-shield fa-4x text-success"></i>
                            <div v-html="$t('org.auth.yourAccountSafeWithMFA')"></div>
                        </div>
                        <b-card-body>
                            <b-button class="float-right" variant="danger" @click="unsetOtp">{{ $t('org.auth.disableMFA') }}</b-button>
                        </b-card-body>
                    </template>
                    <template v-else>
                        <b-card-body>
                            <b-row>
                                <b-col md="9">
                                    <div class="mb-1">{{$t("org.auth.useMFAToIncreaseSecurity")}}</div>
                                    <div v-html="$t('org.auth.downloadAppFromNextLinks')"></div>
                                </b-col>
                                <b-col md="3">
                                    <b-button variant="success" block>
                                        <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" class="text-white">
                                            <i class="fab fa-google-play fa-fw"></i> Google Play
                                        </a>
                                    </b-button>
                                    <b-button variant="info" block>
                                        <a href="https://itunes.apple.com/us/app/google-authenticator/id388497605" target="_blank" class="text-white">
                                            <i class="fab fa-app-store-ios fa-fw"></i> App Store
                                        </a>
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-card-body>
                    </template>
                </b-card>
            </b-col>
        </b-row>
        <b-row v-if="!userInfo.otpEnabled" class="match-height">
            <b-col xl="6" md="12">
                <b-card>
                    <b-badge variant="primary" size="sm">Step 1</b-badge>
                    <span class="ml-1">{{ $t("org.auth.scanQrCodeOrEnterCode") }}</span>
                    <b-card-body class="text-center">
                        <div class="mb-1">
                            <img :src="qrImg" class="border">
                        </div>
                        <div class="d-flex justify-content-center">
                            <b-form-group class="mb-0" label="Secret Key">
                                <b-input-group>
                                    <b-form-input v-model="secret" disabled />
                                    <b-input-group-append>
                                        <b-button
                                            v-clipboard:copy="secret"
                                            v-clipboard:success="onCopy"
                                            v-clipboard:error="onError"
                                            variant="success"
                                        >
                                            Copy!
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>
            <b-col xl="6" md="12">
                <b-card>
                    <b-badge variant="primary" size="sm">Step 2</b-badge>
                    <span class="ml-1">{{ $t("org.auth.enter6DigitNumberTwice") }}</span>
                    <b-card-body class="mt-1">
                        <div class="d-flex justify-content-center" style="height:100px">
                            <b-form-group label-cols-lg="4" label="MFA code 1" style="margin:auto 0">
                                <b-input-group>
                                    <b-form-input v-model="mfaCode1" type="number" :disabled="mfaCode1.length > 6" />
                                    <b-input-group-append v-if="mfaCode1.length > 6">
                                        <b-button @click="inputReset('mfaCode1')">Reset</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                                <b-form-invalid-feedback :state="mfaCode1.length < 7">
                                    {{ $t('org.auth.MFA Code can only be 6 digits') }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </div>
                        <div class="d-flex justify-content-center" style="height:100px">
                            <b-form-group label-cols-lg="4" label="MFA code 2" style="margin:auto 0">
                                <b-input-group>
                                    <b-form-input v-model="mfaCode2" type="number" />
                                    <b-input-group-append v-if="mfaCode2.length > 6">
                                        <b-button @click="inputReset('mfaCode2')">Reset</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                                <b-form-invalid-feedback :state="mfaCode2.length < 7">
                                    {{ $t('org.auth.MFA Code can only be 6 digits') }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </div>
                        <div class="mt-3 d-flex justify-content-center">
                            <b-button variant="primary" class="width-50-per" block :disabled="mfaCode1.length !== 6 && mfaCode2.length !== 6" @click="setOtp">{{ $t('org.auth.confirm') }}</b-button>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getCurrentInstance, computed, ref } from '@vue/composition-api'
import { authenticator } from '@otplib/preset-default'
import qrcode from 'qrcode'
import Ripple from 'vue-ripple-directive'
authenticator.options = {
    encoding: 'hex',
    window: [-2, 0]
}
export default {
    directives: {
        Ripple
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store

        /* data */
        const secret = ref(null)
        const qrImg = ref(null)
        const mfaCode1 = ref('')
        const mfaCode2 = ref('')

        /* computed */
        const userInfo = computed(() => store.getters.userInfo)

        /* methods */
        const getQRCode = () => {
            secret.value = authenticator.generateSecret()
            const otpauth = authenticator.keyuri(userInfo.value.email, 'HyperBilling-Auth', secret.value)
            qrcode.toDataURL(otpauth, (err, imageUrl) => {
                if (err) {
                    //
                }
                qrImg.value = imageUrl
            })
        }
        const onCopy = () => {
            vm.$toast({
                component: ToastificationContent,
                props: {
                    title: 'Text copied',
                    icon: 'BellIcon'
                }
            })
        }
        const onError = () => {
            vm.$toast({
                component: ToastificationContent,
                props: {
                    title: 'Failed to copy texts!',
                    icon: 'BellIcon'
                }
            })
        }
        const inputReset = (inputName) => {
            if (inputName === 'mfaCode1') mfaCode1.value = ''
            else if (inputName === 'mfaCode2') mfaCode2.value = ''
        }
        const setOtp = () => {
            const delta1 = authenticator.checkDelta(mfaCode1.value, secret.value)
            const delta2 = authenticator.checkDelta(mfaCode2.value, secret.value)
            if (Math.abs(delta1 - delta2) !== 1) {
                vm.$toast({
                    component: ToastificationContent,
                    props: {
                        title: vm.$t('org.auth.verificationCodeIncorrect'),
                        icon: 'AlertTriangleIcon',
                        variant: 'warning'
                    }
                })
                return false
            }
            store.dispatch('setOtp', { userIdx: userInfo.value.userIdx, secretKey: secret.value }).then((response) => {
                store.dispatch('createMemberAuthAgain', { email: userInfo.value.email }).then(() => {
                    store.dispatch('getProfileUserInfo', { userIdx: userInfo.value.userIdx }).then(() => {
                        vm.$toast({
                            component: ToastificationContent,
                            props: {
                                title: vm.$t('org.auth.OTP is enabled'),
                                icon: 'CheckCircleIcon',
                                variant: 'success'
                            }
                        })
                    })
                })
            }).catch((error) => {
                if (error.status === 400) {
                    vm.$toast({
                        component: ToastificationContent,
                        props: {
                            title: error.data.errorMessage,
                            icon: 'AlertTriangleIcon',
                            variant: 'warning'
                        }
                    })
                }
            })
        }
        const unsetOtp = () => {
            vm.$bvModal.msgBoxConfirm(vm.$t('org.auth.areYouSureToDisableOTP'), {
                title: vm.$t('org.auth.disableMFA'),
                size: 'sm',
                okVariant: 'primary',
                okTitle: 'Yes',
                cancelTitle: 'No',
                cancelVariant: 'outline-secondary',
                hideHeaderClose: false,
                centered: true
            }).then(value => {
                if (value) {
                    store.dispatch('unsetOtp', { userIdx: userInfo.value.userIdx }).then((response) => {
                        store.dispatch('createMemberAuthAgain', { email: userInfo.value.email }).then(() => {
                            store.dispatch('getProfileUserInfo', { userIdx: userInfo.value.userIdx }).then(() => {
                                vm.$toast({
                                    component: ToastificationContent,
                                    props: {
                                        title: vm.$t('org.auth.OTP is disabled'),
                                        icon: 'CheckCircleIcon',
                                        variant: 'success'
                                    }
                                })
                            })
                        })
                    })
                }
            })
        }

        /* init */
        if (!userInfo.value.otpEnabled) {
            getQRCode()
        }
        return {
            secret,
            qrImg,
            mfaCode1,
            mfaCode2,
            userInfo,
            getQRCode,
            onCopy,
            onError,
            inputReset,
            setOtp,
            unsetOtp
        }
    }
}
</script>

<style>

</style>
